import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import type { ApiClient, CctvListe, CctvSchemaVorschau, CctvZeile } from '../api';
import { SymKamera } from '../ElektroSymbole';
import { IconHerunterladen, IconWarnung } from '../Icons';
import { Aufklappmenue } from '../rahmen/Aufklappmenue';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, Feld, KpiCard, KpiReihe,
  LoadingState, StatusBadge, type Spalte, type Zustand,
} from '../muster';

interface Rahmen { api: ApiClient; projekt?: { id: string } | null }

/**
 * Video / CCTV — die Kameraliste (Etappe 2, docs/CCTV.md).
 *
 * Gezeigt wird der GESCHRIEBENE Stand aus `elektro_cctv_anschluesse`: jede
 * Kamera mit ihrer festen Kennung CCTV-nnnn, ihrem IT-Schrank, Patchfeld und
 * Switch, der Kabellänge mit Herkunft und dem PoE-Ansatz. Gerechnet wird
 * nicht hier, sondern im Lauf `cctv-bilden` — „Probelauf" zeigt, was er
 * schreiben würde, „Schreiben" führt ihn fort. Handwerte bleiben stehen.
 *
 * Der Datenschutzhinweis steht fest auf der Seite (Festlegung G4).
 */

// ═══════════════════════════════════════════════════════════════════════════
// Rechnende Teile — ohne React, deshalb einzeln prüfbar
// ═══════════════════════════════════════════════════════════════════════════

export interface CctvFilter {
  geschoss: string;
  raum: string;
  schrank: string;
  typ: string;
  kameraart: string;
  status: string;
  suche: string;
}

export const LEERER_FILTER: CctvFilter = {
  geschoss: '', raum: '', schrank: '', typ: '', kameraart: '', status: '', suche: '',
};

/** Dieselben Filter wie die API (`GET …/elektro/cctv`), auf der Seite. */
export function cctvFiltern(zeilen: CctvZeile[], f: CctvFilter): CctvZeile[] {
  const s = f.suche.trim().toLowerCase();
  const gleich = (wert: string | null, filter: string) =>
    filter === '' || (wert ?? '').toLowerCase() === filter.toLowerCase();

  return zeilen.filter((z) =>
    gleich(z.geschoss, f.geschoss)
    && gleich(z.raumnummer, f.raum)
    && gleich(z.itSchrank, f.schrank)
    && gleich(z.typ, f.typ)
    && gleich(z.kameraart, f.kameraart)
    && gleich(z.status, f.status)
    && (s === '' || [z.kennung, z.itKennung, z.raumnummer, z.raumname, z.typ,
      z.itSchrank, z.ipPlan, z.bemerkung]
      .some((x) => (x ?? '').toLowerCase().includes(s))));
}

export interface CctvZahlen {
  anzahl: number;
  poeSummeW: number;
  kabelmeter: number;
  ohneLaenge: number;
  fehler: number;
  warnungen: number;
}

/** Kennzahlen über die GEFILTERTEN Kameras — entfallene zählen nicht. */
export function cctvKennzahlen(zeilen: CctvZeile[]): CctvZahlen {
  const aktiv = zeilen.filter((z) => !z.entfernt);
  const runden = (x: number) => Math.round(x * 10) / 10;

  return {
    anzahl: aktiv.length,
    poeSummeW: runden(aktiv.reduce((n, z) => n + (z.poeW ?? 0), 0)),
    kabelmeter: runden(aktiv.reduce((n, z) => n + (z.laengeM ?? 0), 0)),
    ohneLaenge: aktiv.filter((z) => z.laengeM === null).length,
    fehler: aktiv.filter((z) => z.status === 'fehler').length,
    warnungen: aktiv.filter((z) => z.status === 'warnung').length,
  };
}

export function statusZustand(status: string): Zustand {
  switch (status) {
    case 'fehler': return 'fehler';
    case 'warnung': return 'warnung';
    case 'ok': return 'gut';
    case 'entfernt': return 'neutral';
    default: return 'info';
  }
}

export const STATUS_TEXT: Record<string, string> = {
  ok: 'in Ordnung',
  warnung: 'Warnung',
  fehler: 'Fehler',
  entfernt: 'entfallen',
};

/** Woher ein Wert kommt — in Worten statt Schlüsseln. */
export const HERKUNFT_TEXT: Record<string, string> = {
  hand: 'von Hand',
  schaetzung: 'Schätzung',
  trasse: 'Trasse',
  vorgabe: 'Vorgabe',
  it_vorgabe: 'IT-Vorgabe (kamera)',
  muster: 'aus Typname',
  offen: 'offen',
  bauteil: 'Raumbuch',
  zuweisung: 'IT-Zuweisung',
};

export const herkunftText = (h: string | null | undefined) =>
  h ? (HERKUNFT_TEXT[h] ?? h) : '—';

/** Der feste Hinweis (G4) — auch ohne Antwort der API sichtbar. */
export const DATENSCHUTZ_RUECKFALL =
  'Nur technische Planungsdaten: keine personenbezogenen Daten, keine '
  + 'Bildinhalte, keine MAC-Adressen und keine Zugangsdaten. VLAN und '
  + 'IP-Adresse sind unverbindliche Planungswerte.';

export function Datenschutzhinweis({ text }: { text?: string | null }) {
  return (
    <div className="hinweis" role="note" style={{ marginTop: 10 }} data-datenschutz="">
      <IconWarnung size={14} />
      <div style={{ fontSize: 12 }}>
        <strong>Datenschutz:</strong> {text || DATENSCHUTZ_RUECKFALL}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PDF (Etappe 3) — Layout der Plattform, Nummer aus dem Berichtsregister
// ═══════════════════════════════════════════════════════════════════════════

export type CctvPdfArt =
  | 'bericht'
  | 'kameraliste-geschoss'
  | 'kameraliste-schrank'
  | 'kabelliste'
  | 'schema'
  | 'lageplan';

/** Die Einträge des PDF-Menüs, in dieser Reihenfolge. */
export const CCTV_PDF_EINTRAEGE: { art: CctvPdfArt; text: string }[] = [
  { art: 'bericht', text: 'Gesamtbericht ELT-CCTV' },
  { art: 'kameraliste-geschoss', text: 'Kameraliste nach Geschoss' },
  { art: 'kameraliste-schrank', text: 'Kameraliste nach IT-Schrank' },
  { art: 'kabelliste', text: 'Kabelliste je IT-Schrank' },
  { art: 'schema', text: 'Schema aller IT-Schränke' },
  { art: 'lageplan', text: 'Lageplan je Geschoss' },
];

/** Ruft für eine Menüwahl den passenden Endpunkt. */
export function cctvPdfLaden(api: ApiClient, projektId: string, art: CctvPdfArt): Promise<void> {
  switch (art) {
    case 'bericht': return api.elektroCctvBerichtPdf(projektId);
    case 'kameraliste-geschoss': return api.elektroCctvKameralistePdf(projektId, 'geschoss');
    case 'kameraliste-schrank': return api.elektroCctvKameralistePdf(projektId, 'schrank');
    case 'kabelliste': return api.elektroCctvKabellistePdf(projektId);
    case 'schema': return api.elektroCctvSchemaPdf(projektId);
    case 'lageplan': return api.elektroCctvLageplanPdf(projektId);
    default: return Promise.reject(new Error(`unbekannte PDF-Ausgabe ${String(art)}`));
  }
}

/**
 * Das Export-Menü „PDF" neben den Excel-Knöpfen. Jede Ausgabe bekommt im
 * Berichtsregister eine Dokumentnummer — deshalb ein Menü mit Klick und keine
 * Vorschau beim Überfahren.
 */
export function CctvPdfMenue({
  beiWahl, gesperrt = false,
}: {
  beiWahl: (art: CctvPdfArt) => void;
  gesperrt?: boolean;
}) {
  return (
    <Aufklappmenue
      ausloeserBeschriftung="PDF-Ausgaben Video / CCTV"
      ausloeserKlasse="m-knopf"
      ausloeser={(
        <>
          <IconHerunterladen size={13} /> PDF
          <span className="aufklappmenue-pfeil" aria-hidden="true">▾</span>
        </>
      )}
    >
      {CCTV_PDF_EINTRAEGE.map((e) => (
        <button
          key={e.art}
          type="button"
          role="menuitem"
          className="aufklappmenue-punkt"
          data-pdf={e.art}
          disabled={gesperrt}
          onClick={() => beiWahl(e.art)}
        >
          {e.text}
        </button>
      ))}
    </Aufklappmenue>
  );
}

/** Die Id des IT-Schranks zu einem Kennzeichen — aus den Zeilen der Liste. */
export function schrankIdFuer(zeilen: CctvZeile[], kennzeichen: string): string | null {
  if (kennzeichen === '') return null;
  return zeilen.find((z) => z.itSchrank === kennzeichen && z.itSchrankId)?.itSchrankId ?? null;
}

/**
 * Das Schema eines IT-Schranks: SVG-Vorschau mit Blättern und der Knopf
 * „Schema dieses Schranks (PDF)" — dasselbe Muster wie der Schrankaufriss in
 * ElektroItVerteiler. Die Vorschau verbraucht keine Dokumentnummer.
 */
export function CctvSchrankSchema({
  api, projektId, itSchrankId, kennzeichen,
}: {
  api: ApiClient;
  projektId: string;
  itSchrankId: string;
  kennzeichen: string;
}) {
  const [blatt, setBlatt] = useState(1);
  const [bild, setBild] = useState<CctvSchemaVorschau | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedtPdf, setLaedtPdf] = useState(false);

  useEffect(() => { setBlatt(1); }, [itSchrankId]);

  useEffect(() => {
    let alt: string | null = null;
    let aktiv = true;
    setBild(null);
    setFehler(null);

    api.elektroCctvSchrankSchemaSvg(projektId, itSchrankId, blatt)
      .then((v) => {
        alt = v.url;
        if (aktiv) setBild(v);
        else URL.revokeObjectURL(v.url);
      })
      .catch((e) => { if (aktiv) setFehler(e instanceof Error ? e.message : String(e)); });

    // Die Objekt-URL wieder freigeben - sonst hält jeder Blattwechsel ein
    // weiteres Blatt im Speicher.
    return () => { aktiv = false; if (alt) URL.revokeObjectURL(alt); };
  }, [api, projektId, itSchrankId, blatt]);

  const blaetter = bild?.blaetter ?? 1;

  return (
    <ContentSection
      titel={`Schema ${kennzeichen}`}
      beschreibung={'Kamera → Patchfeld → Switch (PoE-Bilanz) → IT-Schrank → Kernverteiler; '
        + 'dieselben Blätter wie im PDF, dort mit Rahmen, Schriftfeld und Dokumentnummer.'}
      meta={bild ? `Blatt ${bild.blatt} von ${blaetter}` : undefined}
      aktionen={(
        <>
          <button type="button" className="m-knopf" disabled={blatt <= 1}
                  onClick={() => setBlatt((b) => Math.max(1, b - 1))}>
            Blatt zurück
          </button>
          <button type="button" className="m-knopf" disabled={blatt >= blaetter}
                  onClick={() => setBlatt((b) => Math.min(blaetter, b + 1))}>
            Blatt vor
          </button>
          <button
            type="button"
            className="m-knopf"
            data-schrank-pdf=""
            disabled={laedtPdf}
            onClick={async () => {
              setLaedtPdf(true);
              try {
                await api.elektroCctvSchrankSchemaPdf(projektId, itSchrankId);
              } catch (e) {
                setFehler(e instanceof Error ? e.message : String(e));
              } finally {
                setLaedtPdf(false);
              }
            }}
          >
            <IconHerunterladen size={13} /> Schema dieses Schranks (PDF)
          </button>
        </>
      )}
    >
      {fehler ? (
        <ErrorState klein meldung="Das Schema ließ sich nicht zeichnen." technisch={fehler} />
      ) : !bild ? (
        <LoadingState text="Schema wird gezeichnet …" />
      ) : (
        <img
          src={bild.url}
          alt={`CCTV-Schema ${kennzeichen}, Blatt ${bild.blatt}`}
          style={{ width: '100%', maxWidth: 1200, border: '1px solid var(--linie)' }}
        />
      )}
    </ContentSection>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Die Übersicht — Filter, Kennzahlen, Tabelle
// ═══════════════════════════════════════════════════════════════════════════

const optionen = (werte: string[], text?: Record<string, string>) =>
  werte.map((w) => ({ wert: w, beschriftung: text?.[w] ?? w }));

export function CctvUebersicht({
  liste, beiOeffnen, schema,
}: {
  liste: CctvListe;
  beiOeffnen: (id: string) => void;
  /** Das Schema des gefilterten IT-Schranks — nur bei gesetztem Schrankfilter. */
  schema?: (itSchrankId: string, kennzeichen: string) => ReactNode;
}) {
  const [filter, setFilter] = useState<CctvFilter>(LEERER_FILTER);
  const schrankId = schrankIdFuer(liste.zeilen, filter.schrank);

  const setze = (feld: keyof CctvFilter) => (w: unknown) =>
    setFilter((f) => ({ ...f, [feld]: typeof w === 'string' ? w : '' }));

  const gefiltert = useMemo(() => cctvFiltern(liste.zeilen, filter), [liste.zeilen, filter]);
  const zahlen = cctvKennzahlen(gefiltert);
  const gefiltertAktiv = Object.values(filter).some((w) => w !== '');

  const spalten: Spalte<CctvZeile>[] = [
    {
      schluessel: 'kennung', titel: 'Kennung', immerSichtbar: true,
      wert: (z) => z.kennung,
      inhalt: (z) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <SymKamera size={14} />
          <span>{z.kennung}</span>
        </span>
      ),
    },
    { schluessel: 'geschoss', titel: 'Geschoss', wert: (z) => z.geschoss ?? '' },
    { schluessel: 'raum', titel: 'Raum', wert: (z) => z.raumnummer ?? '' },
    { schluessel: 'typ', titel: 'Typ', wert: (z) => z.typ ?? '' },
    {
      schluessel: 'kameraart', titel: 'Kameraart',
      wert: (z) => z.kameraart ?? '',
      titelHinweis: 'von Hand, Vorgabe je Typ oder aus dem Typnamen vorgeschlagen',
    },
    { schluessel: 'schrank', titel: 'IT-Schrank', wert: (z) => z.itSchrank ?? '' },
    {
      schluessel: 'patchfeld', titel: 'Patchfeld',
      wert: (z) => (z.patchfeldNr ? `${z.patchfeldNr}/${z.patchfeldPort ?? '–'}` : ''),
    },
    {
      schluessel: 'switch', titel: 'Switch',
      wert: (z) => (z.switchNr ? `${z.switchNr}/${z.switchPort ?? '–'}` : ''),
    },
    {
      schluessel: 'laenge', titel: 'Kabel', einheit: 'm', art: 'zahl', nachkomma: 1,
      wert: (z) => z.laengeM,
    },
    {
      schluessel: 'laengeHerkunft', titel: 'Länge aus',
      wert: (z) => herkunftText(z.laengeHerkunft),
    },
    {
      schluessel: 'poe', titel: 'PoE', einheit: 'W', art: 'zahl', nachkomma: 1,
      wert: (z) => z.poeW,
    },
    { schluessel: 'vlan', titel: 'VLAN (Plan)', versteckt: true, wert: (z) => z.vlan ?? '' },
    { schluessel: 'ip', titel: 'IP (Plan)', versteckt: true, wert: (z) => z.ipPlan ?? '' },
    {
      schluessel: 'status', titel: 'Status', art: 'status',
      wert: (z) => z.status,
      inhalt: (z) => (
        <StatusBadge
          zustand={statusZustand(z.status)}
          text={STATUS_TEXT[z.status] ?? z.status}
          klein
        />
      ),
    },
  ];

  const a = liste.auswahl;

  return (
    <>
      <KpiReihe>
        <KpiCard titel="Kameras" wert={zahlen.anzahl}
                 vergleich={gefiltertAktiv ? `von ${liste.kennzahlen.anzahl}` : undefined} />
        <KpiCard titel="PoE-Summe" wert={zahlen.poeSummeW} einheit="W" nachkomma={1}
                 fussnote="Ansatz je Kameraart — kein Messwert" />
        <KpiCard titel="Kabelmeter" wert={zahlen.kabelmeter} einheit="m" nachkomma={1}
                 fussnote={zahlen.ohneLaenge > 0
                   ? `${zahlen.ohneLaenge} Kamera(s) ohne Länge`
                   : 'jede Kamera mit Länge'} />
        <KpiCard
          titel="Befunde"
          wert={zahlen.fehler + zahlen.warnungen}
          zustand={zahlen.fehler > 0 ? 'fehler' : zahlen.warnungen > 0 ? 'warnung' : 'gut'}
          zustandText={zahlen.fehler > 0
            ? `${zahlen.fehler} Fehler`
            : zahlen.warnungen > 0 ? `${zahlen.warnungen} Warnung(en)` : 'keine'}
          fussnote="Kameras mit Fehler oder Warnung (K01–K10)"
        />
      </KpiReihe>

      <Datenschutzhinweis text={liste.datenschutz} />

      <ContentSection
        titel="Kameras"
        meta={`${gefiltert.length} von ${liste.zeilen.length}`}
        werkzeuge={(
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <Feld beschriftung="Suche" art="suche" breite="drittel"
                  wert={filter.suche} beiAenderung={setze('suche')}
                  platzhalter="Kennung, Raum, Typ, IP …" />
            <Feld beschriftung="Geschoss" art="auswahl" breite="drittel"
                  wert={filter.geschoss} beiAenderung={setze('geschoss')}
                  optionen={optionen(a.geschosse)} platzhalter="alle" />
            <Feld beschriftung="Raum" art="auswahl" breite="drittel"
                  wert={filter.raum} beiAenderung={setze('raum')}
                  optionen={optionen(a.raeume)} platzhalter="alle" />
            <Feld beschriftung="IT-Schrank" art="auswahl" breite="drittel"
                  wert={filter.schrank} beiAenderung={setze('schrank')}
                  optionen={optionen(a.schraenke)} platzhalter="alle" />
            <Feld beschriftung="Typ" art="auswahl" breite="drittel"
                  wert={filter.typ} beiAenderung={setze('typ')}
                  optionen={optionen(a.typen)} platzhalter="alle" />
            <Feld beschriftung="Kameraart" art="auswahl" breite="drittel"
                  wert={filter.kameraart} beiAenderung={setze('kameraart')}
                  optionen={optionen(a.kameraarten)} platzhalter="alle" />
            <Feld beschriftung="Status" art="auswahl" breite="drittel"
                  wert={filter.status} beiAenderung={setze('status')}
                  optionen={optionen(a.status, STATUS_TEXT)} platzhalter="alle" />
            {gefiltertAktiv && (
              <button type="button" className="m-knopf m-knopf-still"
                      onClick={() => setFilter(LEERER_FILTER)}>
                Filter zurücksetzen
              </button>
            )}
          </div>
        )}
        nackt
      >
        <DataTable
          name="Kameras"
          spalten={spalten}
          zeilen={gefiltert}
          schluessel={(z) => z.id}
          suche={false}
          ausfuhr
          ausfuhrDateiname="cctv-kameras.csv"
          dicht
          beiZeileKlick={(z) => beiOeffnen(z.id)}
          leer={(
            <EmptyState
              titel="Keine Kamera passt zu den Filtern."
              erklaerung="Filter zurücksetzen oder die Suche lockern."
              klein
            />
          )}
        />
      </ContentSection>

      {schema && schrankId && (
        <div data-schrank-schema={filter.schrank}>
          {schema(schrankId, filter.schrank)}
        </div>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Die Seite
// ═══════════════════════════════════════════════════════════════════════════

export function ElektroCctv() {
  const { api, projekt } = useOutletContext<Rahmen>();
  const navigate = useNavigate();

  const [liste, setListe] = useState<CctvListe | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  const laden = useCallback(() => {
    if (!projekt) return;
    setFehler(null);

    api.elektroCctv(projekt.id)
      .then(setListe)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projekt?.id]);

  useEffect(() => { laden(); }, [laden]);

  const bilden = async (schreiben: boolean) => {
    if (!projekt) return;
    setLaeuft(true);
    setMeldung(null);

    try {
      const e = await api.elektroCctvBilden(projekt.id, schreiben);
      setMeldung(e.message);
      if (schreiben) laden();
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  const laden_ = async (art: 'kamera' | 'kabel') => {
    if (!projekt) return;

    try {
      if (art === 'kamera') await api.elektroCctvKameraliste(projekt.id);
      else await api.elektroCctvKabelliste(projekt.id);
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    }
  };

  const [pdfLaeuft, setPdfLaeuft] = useState(false);

  const pdf = async (art: CctvPdfArt) => {
    if (!projekt) return;
    setPdfLaeuft(true);

    try {
      await cctvPdfLaden(api, projekt.id, art);
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setPdfLaeuft(false);
    }
  };

  const kopf = (
    <PageHeader
      titel="Elektro — Video / CCTV"
      beschreibung={'Kameras mit fester Kennung, IT-Schrank, Patchfeld und Switch, '
        + 'Kabellänge und PoE. Gerechnet im Lauf „cctv-bilden"; Handwerte bleiben '
        + 'beim nächsten Lauf stehen.'}
      meta={liste ? `${liste.gesamt} Kameras` : undefined}
      aktionen={(
        <>
          <button type="button" className="m-knopf" disabled={laeuft || !projekt}
                  onClick={() => bilden(false)}>
            Probelauf
          </button>
          <button type="button" className="m-knopf m-knopf-haupt" disabled={laeuft || !projekt}
                  onClick={() => bilden(true)}>
            Schreiben
          </button>
          <button type="button" className="m-knopf" disabled={!projekt}
                  onClick={() => laden_('kamera')}>
            <IconHerunterladen size={13} /> Kameraliste
          </button>
          <button type="button" className="m-knopf" disabled={!projekt}
                  onClick={() => laden_('kabel')}>
            <IconHerunterladen size={13} /> Kabelliste
          </button>
          <CctvPdfMenue beiWahl={pdf} gesperrt={!projekt || pdfLaeuft} />
        </>
      )}
    />
  );

  if (fehler) {
    return (
      <>
        {kopf}
        <ErrorState
          meldung="Die Kameras konnten nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={laden}
        />
      </>
    );
  }

  if (!projekt || !liste) {
    return (
      <>
        {kopf}
        <LoadingState text="Kameras werden geladen …" zeilen={8} spalten={6} />
      </>
    );
  }

  return (
    <>
      {kopf}

      {meldung && (
        <div className="hinweis" role="status" style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12 }}>{meldung}</div>
        </div>
      )}

      {liste.gesamt === 0 ? (
        <>
          <Datenschutzhinweis text={liste.datenschutz} />
          <EmptyState
            titel="Noch keine Kamera gebildet."
            erklaerung={'„Probelauf" zeigt, was der Lauf schreiben würde; „Schreiben" '
              + 'legt die Kameras mit Kennung CCTV-nnnn an (Werkzeug: '
              + 'cctv-bilden <projektId> --schreiben).'}
          />
        </>
      ) : (
        <CctvUebersicht
          liste={liste}
          beiOeffnen={(id) => navigate(`../elektro-cctv/${id}`)}
          schema={(itSchrankId, kennzeichen) => (
            <CctvSchrankSchema
              api={api}
              projektId={projekt.id}
              itSchrankId={itSchrankId}
              kennzeichen={kennzeichen}
            />
          )}
        />
      )}
    </>
  );
}
